import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { LoaderData } from "~/types";
import { INTENTS, loadCookiesData } from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import { message } from "antd";
import { t } from "i18next";
import {
  apiClient,
  AddUnitPageLoaderResult,
  UnitPlanTableLoaderResult,
} from "~/services/apiClient";

export const AddUnitPageLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<AddUnitPageLoaderType> => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) redirect("/explore");
  const userUID = user!.uid;
  const authAccessInfo = await loadCookiesData(userUID);
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const { searchParams } = new URL(request.url);
  const initialBuildingID = searchParams.get("buildingID") ?? "";

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const [result, unitPlansResult] = await Promise.all([
    apiClient.loader<AddUnitPageLoaderResult>("add-unit-page", {
      ownerID,
      ownerType,
      userID: userUID,
      initialBuildingID: initialBuildingID || undefined,
      isAdminOrSuperAdmin: String(isAdminOrSuperAdmin),
    }),
    apiClient.loader<UnitPlanTableLoaderResult>("unit-plan-table", {
      ownerID,
      ownerType,
      userID: userUID,
    }),
  ]);

  if (!result.success || !result.data) {
    console.error("Failed to load add unit page:", result.error);
    throw new Error(result.error ?? "Failed to load add unit page");
  }

  const data = result.data;
  const unitPlanList =
    unitPlansResult.success && unitPlansResult.data
      ? unitPlansResult.data.unitPlanList
      : [];
  const buildingList: LoaderData<APITypes.Building[]> = {
    success: true,
    data: data.buildingList,
  };

  return {
    buildingList,
    propertyMap: data.propertyMap,
    initialBuilding: data.initialBuilding,
    canCreateUnit: data.canCreateUnit,
    isAuthenticated: true,
    subscriptionPlan: data.subscriptionPlan,
    currentUnitCount: data.currentUnitCount,
    maxUnits: data.maxUnits,
    unitPlanList,
  };
};

export type AddUnitPageLoaderType = {
  buildingList: LoaderData<APITypes.Building[]>;
  propertyMap: Record<string, APITypes.Property>;
  initialBuilding: APITypes.Building;
  canCreateUnit: boolean;
  isAuthenticated: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  currentUnitCount: number;
  maxUnits: number;
  unitPlanList: UnitPlanTableLoaderResult["unitPlanList"];
};

export const AddUnitPageAction = async ({ request }: LoaderFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { role } = authAccessInfo.data;

  switch (intent) {
    case INTENTS._add_unit: {
      return withEntityCacheInvalidation([{ entity: "unit" }], async () => {
        const payload = JSON.parse(
          formData.get("body") as string
        ) as Record<string, unknown>;

        const isAdminOrSuperAdmin =
          role === APITypes.ManagerRolesType.SuperAdmin ||
          role === APITypes.ManagerRolesType.Admin;

        const result = await apiClient.sendIntent<{
          id?: string;
          buildingID: string;
          created: number;
        }>(INTENTS._add_unit, {
          ...payload,
          userID: user.uid,
          isAdminOrSuperAdmin,
        });

        if (!result.success) {
          return {
            success: false,
            error: result.error ?? t("Error_creating_unit"),
          };
        }

        const data = result.data;
        if (!data) {
          return { success: false, error: t("Error_creating_unit") };
        }

        if (data.created > 1) {
          message.success(t("Units_created_successfully"), data.created);
          return redirect(`/building/${data.buildingID}`);
        }

        message.success(t("Unit_created_successfully"));
        const { searchParams } = new URL(request.url);
        const buildingID = searchParams.get("buildingID");
        if (buildingID) {
          return redirect(`/building/${buildingID}`);
        }
        if (data.id) {
          return redirect(`/unit/${data.id}`);
        }
        return redirect(`/building/${data.buildingID}`);
      });
    }
  }
  return {};
};
